import {
  and,
  eq,
  follow,
  isNull,
  type PowerSyncDrizzleDB,
  social_notification,
} from "@famidex/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { getFollowEdgeQuery } from "./queries";

export interface FollowProfileOpts {
  followerName?: string | null;
  followerImage?: string | null;
  followeeName?: string | null;
  followeeImage?: string | null;
}

export interface FollowResult {
  id: string;
  /** true si se creó una arista nueva (false si ya seguía). */
  created: boolean;
  /** true si el seguido YA me seguía → amistad recíproca (dispara +100 XP, Wave 2/E). */
  reciprocalNow: boolean;
}

/**
 * 👥 SEGUIR — patrón UPDATE-or-INSERT por par (follower, followee) sobre la
 * VISTA local-first (las vistas PowerSync no admiten UPSERT, gotcha #15). El
 * servidor reaplica por id con el `follower_id` ESTAMPADO (aislamiento) y GENERA
 * la notificación de follow para el seguido. Devolvemos `reciprocalNow` para que
 * Wave 2/E cablee el +100 XP de amistad.
 */
export async function followUserMutation(
  db: PowerSyncDrizzleDB,
  followerId: string,
  followeeId: string,
  opts?: FollowProfileOpts,
): Promise<FollowResult> {
  if (followerId === followeeId) {
    return { id: "", created: false, reciprocalNow: false };
  }
  const existing = await getFollowEdgeQuery(db, followerId, followeeId);
  if (existing[0]) {
    return { id: existing[0].id, created: false, reciprocalNow: false };
  }
  const id = generateUUID();
  await db.insert(follow).values({
    id,
    follower_id: followerId,
    followee_id: followeeId,
    follower_name: opts?.followerName ?? null,
    follower_image: opts?.followerImage ?? null,
    followee_name: opts?.followeeName ?? null,
    followee_image: opts?.followeeImage ?? null,
    created_at: nowISO(),
  });
  const reverse = await getFollowEdgeQuery(db, followeeId, followerId);
  return { id, created: true, reciprocalNow: Boolean(reverse[0]) };
}

/** Dejar de seguir: retira la arista (follower→followee). Sólo el follower puede. */
export async function unfollowUserMutation(
  db: PowerSyncDrizzleDB,
  followerId: string,
  followeeId: string,
): Promise<void> {
  await db
    .delete(follow)
    .where(and(eq(follow.follower_id, followerId), eq(follow.followee_id, followeeId)));
}

/** Marca UNA notificación como leída (owner-scoped). */
export async function markNotificationReadMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  await db
    .update(social_notification)
    .set({ read_at: nowISO() })
    .where(and(eq(social_notification.user_id, userId), eq(social_notification.id, id)));
}

/** Marca TODAS mis notificaciones sin leer como leídas. */
export async function markAllNotificationsReadMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
): Promise<void> {
  await db
    .update(social_notification)
    .set({ read_at: nowISO() })
    .where(and(eq(social_notification.user_id, userId), isNull(social_notification.read_at)));
}

/** Descarta (borra) una notificación mía. */
export async function dismissNotificationMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  await db
    .delete(social_notification)
    .where(and(eq(social_notification.user_id, userId), eq(social_notification.id, id)));
}
